<template>
  <div class="integrations-panel">
    <div class="integrations-panel__header">
      <h1>Integrations</h1>
      <p>Cloudflare, GitLab and Enom credentials Goshawk uses for provisioning — entered here instead of environment variables so they can be rotated without a redeploy.</p>
    </div>

    <div v-if="loadError" class="integrations-panel__error">{{ loadError }}</div>

    <!-- Plan 010 risk 13 / PF-12 — secrets-encryption visibility. Writes are
         now refused server-side when DAEDALUS_SECRETS_KEY isn't configured
         (GuardedSecretStore), but that refusal is only half a fix if nobody
         can SEE why: this banner is the other half. -->
    <div v-if="encryption && !encryption.configured" class="integrations-panel__warning">
      <strong>Secrets encryption is not configured.</strong> Credential values below cannot be saved until <code>DAEDALUS_SECRETS_KEY</code> is set (see <code>api/config/secrets.php</code>).
    </div>
    <div v-if="encryption && plaintextFindingsCount" class="integrations-panel__warning">
      <strong>{{ plaintextFindingsCount }} stored credential{{ plaintextFindingsCount === 1 ? '' : 's' }} {{ plaintextFindingsCount === 1 ? 'is' : 'are' }} unprotected (plaintext at rest)</strong> — written before encryption was configured.
      <template v-if="encryption.configured">
        <button
          type="button"
          class="integrations-panel__btn integrations-panel__btn--primary integrations-panel__reencrypt-btn"
          :disabled="reencrypting"
          @click="reencryptPlaintext"
        >{{ reencrypting ? 'Re-encrypting…' : 'Re-encrypt now' }}</button>
      </template>
      <template v-else> Set the key above, then re-encrypt them.</template>
    </div>

    <!-- Cloudflare -->
    <section class="integrations-panel__card">
      <div class="integrations-panel__card-head">
        <h3>Cloudflare</h3>
        <span
          class="integrations-panel__pill"
          :class="credentialPillClass('domains_cloudflare_token')"
        >{{ credentialPillText('domains_cloudflare_token') }}</span>
      </div>
      <p class="integrations-panel__card-desc">API token used for DNS zone/record automation (Plan 005 M2).</p>

      <form class="integrations-panel__form" @submit.prevent="setCredential('domains_cloudflare_token')">
        <input
          v-model="tokenInputs.domains_cloudflare_token"
          type="password"
          autocomplete="off"
          placeholder="Cloudflare API token"
          class="integrations-panel__input"
        />
        <button
          type="submit"
          class="integrations-panel__btn integrations-panel__btn--primary"
          :disabled="!tokenInputs.domains_cloudflare_token || savingKey === 'domains_cloudflare_token'"
        >{{ credentialIsSet('domains_cloudflare_token') ? 'Replace' : 'Set' }}</button>
        <button
          v-if="credentialIsSet('domains_cloudflare_token')"
          type="button"
          class="integrations-panel__btn integrations-panel__btn--danger"
          :disabled="clearingKey === 'domains_cloudflare_token'"
          @click="clearCredential('domains_cloudflare_token')"
        >Clear</button>
      </form>
    </section>

    <!-- Enom -->
    <section class="integrations-panel__card">
      <div class="integrations-panel__card-head">
        <h3>Enom</h3>
        <span
          class="integrations-panel__pill"
          :class="enomPillClass"
        >{{ enomPillText }}</span>
      </div>
      <p class="integrations-panel__card-desc">Reseller account UID + API token used for registrar reads/writes (Plan 005 M3 domain inventory sync). Both must be set before Enom calls are attempted — until then the sync skips Enom cleanly.</p>

      <form class="integrations-panel__form" @submit.prevent="setCredential('domains_enom_uid')">
        <input
          v-model="tokenInputs.domains_enom_uid"
          type="password"
          autocomplete="off"
          placeholder="Enom account UID"
          class="integrations-panel__input"
        />
        <button
          type="submit"
          class="integrations-panel__btn integrations-panel__btn--primary"
          :disabled="!tokenInputs.domains_enom_uid || savingKey === 'domains_enom_uid'"
        >{{ credentialIsSet('domains_enom_uid') ? 'Replace' : 'Set' }}</button>
        <button
          v-if="credentialIsSet('domains_enom_uid')"
          type="button"
          class="integrations-panel__btn integrations-panel__btn--danger"
          :disabled="clearingKey === 'domains_enom_uid'"
          @click="clearCredential('domains_enom_uid')"
        >Clear</button>
      </form>

      <form class="integrations-panel__form integrations-panel__form--stacked" @submit.prevent="setCredential('domains_enom_token')">
        <input
          v-model="tokenInputs.domains_enom_token"
          type="password"
          autocomplete="off"
          placeholder="Enom API token"
          class="integrations-panel__input"
        />
        <button
          type="submit"
          class="integrations-panel__btn integrations-panel__btn--primary"
          :disabled="!tokenInputs.domains_enom_token || savingKey === 'domains_enom_token'"
        >{{ credentialIsSet('domains_enom_token') ? 'Replace' : 'Set' }}</button>
        <button
          v-if="credentialIsSet('domains_enom_token')"
          type="button"
          class="integrations-panel__btn integrations-panel__btn--danger"
          :disabled="clearingKey === 'domains_enom_token'"
          @click="clearCredential('domains_enom_token')"
        >Clear</button>
      </form>
    </section>

    <!-- GitLab -->
    <section class="integrations-panel__card">
      <div class="integrations-panel__card-head">
        <h3>GitLab</h3>
        <span
          class="integrations-panel__pill"
          :class="credentialPillClass('gitlab_api_key')"
        >{{ credentialPillText('gitlab_api_key') }}</span>
      </div>
      <p class="integrations-panel__card-desc">Connection settings + API token used for repo/deploy-key/CI provisioning (Plan 005 M2).</p>

      <form class="integrations-panel__settings-form" @submit.prevent="saveGitLabSettings">
        <div class="integrations-panel__field">
          <label>Base URL</label>
          <input v-model="gitlabSettings.base_url" type="text" placeholder="https://gitlab.apexl" class="integrations-panel__input" />
        </div>
        <div class="integrations-panel__field">
          <label>Template project ID</label>
          <input v-model.number="gitlabSettings.template_project_id" type="number" min="0" class="integrations-panel__input" />
        </div>
        <div class="integrations-panel__field">
          <label>Default namespace ID</label>
          <input v-model.number="gitlabSettings.default_namespace_id" type="number" min="0" class="integrations-panel__input" />
        </div>
        <button type="submit" class="integrations-panel__btn integrations-panel__btn--primary" :disabled="savingSettings">
          {{ savingSettings ? 'Saving…' : 'Save settings' }}
        </button>
      </form>

      <form class="integrations-panel__form" @submit.prevent="setCredential('gitlab_api_key')">
        <input
          v-model="tokenInputs.gitlab_api_key"
          type="password"
          autocomplete="off"
          placeholder="GitLab API token"
          class="integrations-panel__input"
        />
        <button
          type="submit"
          class="integrations-panel__btn integrations-panel__btn--primary"
          :disabled="!tokenInputs.gitlab_api_key || savingKey === 'gitlab_api_key'"
        >{{ credentialIsSet('gitlab_api_key') ? 'Replace' : 'Set' }}</button>
        <button
          v-if="credentialIsSet('gitlab_api_key')"
          type="button"
          class="integrations-panel__btn integrations-panel__btn--danger"
          :disabled="clearingKey === 'gitlab_api_key'"
          @click="clearCredential('gitlab_api_key')"
        >Clear</button>
      </form>
    </section>
  </div>
</template>

<script setup>
// Credentials & Integrations admin page — mounted at `/integrations` by
// `app\module\secrets\Controller\IntegrationsDisplayController::index()`
// (`IntegrationsPanel` PHP Component -> this file, matched by name per
// icarus's require.context auto-registration). Backed by
// `app\module\secrets\Controller\IntegrationsController` /
// `routes/api/secrets.php`. Mirrors `ServersFleetBoard.vue`'s
// whole-owned-page shape and `AdoptionKeysPanel.vue`'s
// convention-driven-CRUD-no-mockup-needed pattern (Plan 003 §2a design-gate
// note) — a form this size doesn't need a Claude Design pass.
//
// Write-only contract: the token inputs are NEVER pre-filled from the
// status response (the API never returns a value in any form, masked or
// otherwise) — after a successful set/clear the input is blanked and only
// the status pill updates.
import { computed, onMounted, reactive, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';

const props = defineProps({
  apiBase: {
    type: String,
    default: '/api/v1/secrets/integrations',
  },
});

const api = useApi();

const loading = ref(false);
const loadError = ref(null);
const credentials = ref([]); // [{key,label,set,updated_at}]
const encryption = ref(null); // {configured, plaintext_findings:[{key,resource_type,resource_id}]}
// Defensive: a malformed payload could hand back something other than an
// array (or omit the field) — never let a `.length` on it throw mid-render.
const plaintextFindingsCount = computed(() => (
  Array.isArray(encryption.value?.plaintext_findings) ? encryption.value.plaintext_findings.length : 0
));
const reencrypting = ref(false);
const gitlabSettings = reactive({ base_url: '', template_project_id: 0, default_namespace_id: 0 });
const tokenInputs = reactive({
  domains_cloudflare_token: '',
  gitlab_api_key: '',
  domains_enom_uid: '',
  domains_enom_token: '',
});
const savingKey = ref(null);
const clearingKey = ref(null);
const savingSettings = ref(false);

// Enom needs BOTH keys before `EnomRegistrar::isAvailable()` is true, so the
// card's single pill reports the pair's combined readiness rather than one
// key's status.
const enomFullyConfigured = computed(() => (
  credentialIsSet('domains_enom_uid') && credentialIsSet('domains_enom_token')
));

const enomPillClass = computed(() => (
  enomFullyConfigured.value ? 'integrations-panel__pill--set' : 'integrations-panel__pill--unset'
));

const enomPillText = computed(() => {
  if (enomFullyConfigured.value) return 'Configured';
  if (credentialIsSet('domains_enom_uid') || credentialIsSet('domains_enom_token')) return 'Partially set';
  return 'Not set';
});

function credentialStatus(key) {
  return credentials.value.find((c) => c.key === key) || null;
}

function credentialIsSet(key) {
  return !!credentialStatus(key)?.set;
}

function credentialPillClass(key) {
  return credentialIsSet(key) ? 'integrations-panel__pill--set' : 'integrations-panel__pill--unset';
}

function credentialPillText(key) {
  const status = credentialStatus(key);
  if (!status?.set) return 'Not set';
  return status.updated_at ? `Set · updated ${relativeTime(status.updated_at)}` : 'Set';
}

function relativeTime(epochSeconds) {
  const seconds = Math.max(0, Math.floor(Date.now() / 1000) - epochSeconds);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

async function loadStatus() {
  loading.value = true;
  loadError.value = null;
  try {
    const data = await api.get(props.apiBase);
    credentials.value = data.credentials || [];
    Object.assign(gitlabSettings, data.gitlab_settings || {});
    encryption.value = data.encryption || null;
  } catch (err) {
    loadError.value = 'Could not load integration status.';
  } finally {
    loading.value = false;
  }
}

async function reencryptPlaintext() {
  reencrypting.value = true;
  loadError.value = null;
  try {
    const data = await api.post(`${props.apiBase}/reencrypt`, {});
    encryption.value = data.encryption || encryption.value;
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Could not re-encrypt those credentials.';
  } finally {
    reencrypting.value = false;
  }
}

async function setCredential(key) {
  const value = tokenInputs[key];
  if (!value) return;
  savingKey.value = key;
  loadError.value = null;
  try {
    const data = await api.post(`${props.apiBase}/credentials/${key}`, { value });
    upsertCredential(data.credential);
    tokenInputs[key] = '';
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Could not save that credential.';
  } finally {
    savingKey.value = null;
  }
}

async function clearCredential(key) {
  clearingKey.value = key;
  loadError.value = null;
  try {
    const data = await api.delete(`${props.apiBase}/credentials/${key}`);
    upsertCredential(data.credential);
    tokenInputs[key] = '';
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Could not clear that credential.';
  } finally {
    clearingKey.value = null;
  }
}

function upsertCredential(status) {
  if (!status) return;
  const idx = credentials.value.findIndex((c) => c.key === status.key);
  if (idx === -1) {
    credentials.value.push(status);
  } else {
    credentials.value.splice(idx, 1, status);
  }
}

async function saveGitLabSettings() {
  savingSettings.value = true;
  loadError.value = null;
  try {
    const data = await api.put(`${props.apiBase}/gitlab-settings`, {
      base_url: gitlabSettings.base_url,
      template_project_id: gitlabSettings.template_project_id,
      default_namespace_id: gitlabSettings.default_namespace_id,
    });
    Object.assign(gitlabSettings, data.gitlab_settings || {});
  } catch (err) {
    loadError.value = err?.response?.data?.error || 'Could not save GitLab settings.';
  } finally {
    savingSettings.value = false;
  }
}

onMounted(loadStatus);
</script>

<style lang="scss">
.integrations-panel {
  padding: 24px;
  max-width: 720px;

  &__header {
    margin-bottom: 20px;

    h1 {
      margin: 0 0 6px;
      font-size: 20px;
    }

    p {
      margin: 0;
      font-size: 12.5px;
      color: var(--theme-text-secondary);
      max-width: 62ch;
      line-height: 1.5;
    }
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 8px 12px;
    font-size: 12px;
    margin-bottom: 16px;
  }

  &__warning {
    background: #fbeecd;
    color: #8a6116;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 12px;
    font-size: 12px;
    line-height: 1.5;
    margin-bottom: 12px;

    code {
      background: rgba(0, 0, 0, .08);
      border-radius: 3px;
      padding: 1px 5px;
      font-size: 11px;
    }
  }

  &__reencrypt-btn {
    display: block;
    margin-top: 8px;
  }

  &__card {
    background: var(--theme-bg-card);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-panel);
    padding: 18px 20px;
    margin-bottom: 18px;
  }

  &__card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 4px;

    h3 {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
    }
  }

  &__card-desc {
    margin: 0 0 14px;
    font-size: 12px;
    color: var(--theme-text-tertiary);
  }

  &__pill {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: .02em;
    padding: 3px 9px;
    border-radius: 3px;
    white-space: nowrap;

    &--set {
      background: var(--theme-status-success);
      color: #fff;
    }

    &--unset {
      background: var(--theme-border-soft);
      color: var(--theme-text-tertiary);
    }
  }

  &__form {
    display: flex;
    gap: 10px;

    &--stacked {
      margin-top: 10px;
    }
  }

  &__settings-form {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
    margin-bottom: 14px;
    padding-bottom: 14px;
    border-bottom: 1px solid var(--theme-border-soft);
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 11.5px;

    label {
      font-weight: 600;
      color: var(--theme-text-secondary);
    }
  }

  &__input {
    flex: 1;
    font-size: 12.5px;
    padding: 8px 12px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-content);
    color: var(--theme-text-primary);
    min-width: 0;
  }

  &__btn {
    font-size: 12.5px;
    font-weight: 600;
    padding: 8px 14px;
    border-radius: var(--theme-radius, 6px);
    cursor: pointer;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    white-space: nowrap;

    &--primary {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
      color: #fff;
    }

    &--danger {
      color: var(--theme-status-danger, #a53232);
    }

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }
}
</style>
